<template>
  <div :data-test="`simple_requisite_rule_${depth}`">
    <div v-if="isEditing">
      <div class="row d-flex">
        <div class="mx-1 pl-0 mb-2 flex-grow">
          <FormSelect
            :disabled="disabled"
            :config="{
              options: conditionOptions,
              multiple: false,
            }"
            :searchable="true"
            label="Rule Condition"
            placeholder="Select condition"
            :value="rule.condition"
            :custom-label="conditionLabel"
            data-test="requisite_rule_condition_selector"
            @input="updateRuleField('condition', $event)"
          />
        </div>
        <div
          v-if="shouldShowGradeInputs"
          class="flex-grow mx-1 mb-2"
        >
          <FormText
            label="Grade Type"
            placeholder="Set Grade Type (i.e. G.P.A)"
            :value="rule.gradeType"
            @input="updateRuleField('gradeType', $event)"
          />
        </div>
        <div
          v-if="shouldShowGradeInputs"
          class="flex-grow mx-1 mb-2"
        >
          <FormText
            label="Grade Value"
            placeholder="Set Grade (i.e. 4.0)"
            :value="rule.grade"
            @input="updateRuleField('grade', $event)"
          />
        </div>
        <div
          v-if="rule.condition === 'minimumCredits'"
          class="flex-grow mx-1 mb-2"
        >
          <FormNumber
            label="Min Credits Value"
            type="number"
            min="0"
            placeholder="Set Min Credits"
            :value="rule.credits"
            @input="updateRuleField('credits', $event)"
          />
        </div>
        <div
          v-if="rule.condition === 'minimumResidencyCredits'"
          class="flex-grow mx-1 mb-2"
        >
          <FormNumber
            label="Min Residency Credits Value"
            type="number"
            min="0"
            placeholder="Set Min Residency Credits"
            :value="rule.credits"
            @input="updateRuleField('credits', $event)"
          />
        </div>
        <div
          v-else-if="rule.condition === 'minimumGPA'"
          class="d-flex"
        >
          Achieved a minimum GPA of
          <FormText
            label="Min GPA Value"
            :value="rule.value"
            placeholder="GPA"
            @input="updateRuleField('value', $event)"
          />
        </div>
      </div>

      <div
        v-if="rule.condition === 'freeformText'"
        class=""
      >
        <FormTextarea
          class="mx-1 pb-3"
          :value="rule.value"
          :rows="1"
          placeholder="Enter Text..."
          @input="updateRuleField('value', $event)"
        />
      </div>

      <div
        v-if="shouldShowDataSelect"
        class="row d-flex"
      >
        <div class="flex-grow">
          <div
            v-if="rule.condition==='completedAtLeastXOf'"
            class="px-0 pb-3"
          >
            <div class="px-1 flex-grow">
              <FormNumber
                :disabled="disabled"
                label="Complete this many to fulfull requirement"
                placeholder="Set number"
                :value="rule.restriction"
                @input="updateRuleField('restriction', $event)"
              />
            </div>
          </div>
          <div class="">
            <SimpleRequisiteDataSelect
              class=""
              :isProgram="isProgram"
              :disabled="disabled"
              :read-only="readOnly"
              :type="rule.condition"
              :selection-data="rule.value"
              :titleInterpolation="titleInterpolation"
              @updatedSelection="updateRuleField('value', $event)"
            />
          </div>
        </div>
      </div>
      <div
        v-if="rule.condition==='completeVariableCoursesAndVariableCredits'"
        class="px-0 py-3"
      >
        <div class="d-flex flex-row">
          <div class="px-1 flex-grow">
            <FormLabel
              label="Number of Course(s)"
            />
            <div class="d-flex flex-row">
              <FormNumber
                class="flex-grow"
                :disabled="disabled"
                label="Min"
                placeholder="Set value"
                :value="rule.minCourses"
                :config="{ max: rule.maxCourses }"
                @input="updateRuleField('minCourses', $event)"
              />
              <FormNumber
                :disabled="disabled"
                class="ml-1 flex-grow"
                label="Max"
                placeholder="Set value"
                :value="rule.maxCourses"
                :config="{ min: rule.minCourses }"
                @input="updateRuleField('maxCourses', $event)"
              />
            </div>
          </div>
          <div class="px-1 flex-grow">
            <FormLabel
              label="Number of Credit(s)"
            />
            <div class="d-flex flex-row">
              <FormNumber
                class="flex-grow"
                :disabled="disabled"
                label="Min"
                placeholder="Set value"
                :value="rule.minCredits"
                :config="{ max: rule.maxCredits }"
                @input="updateRuleField('minCredits', $event)"
              />
              <FormNumber
                :disabled="disabled"
                class="ml-1 flex-grow"
                label="Max"
                placeholder="Set value"
                :value="rule.maxCredits"
                :config="{ min: rule.minCredits }"
                @input="updateRuleField('maxCredits', $event)"
              />
            </div>
          </div>
        </div>
        <div>
          <FormLabel
            class="px-1 pt-3"
            label="Select Course(s)"
          />
          <SimpleRequisiteDataSelect
            :disabled="disabled"
            :read-only="readOnly"
            :type="rule.condition"
            :selection-data="rule.value"
            :show-condition-options="false"
            :titleInterpolation="titleInterpolation"
            default-condition-option="courses"
            @updatedSelection="updateRuleField('value', $event)"
          />
        </div>
      </div>

      <FormText
        v-if="isRulesFieldVisible('ruleName')"
        :disabled="!isRulesFieldEditable('ruleName')"
        class="mx-1 pb-2"
        label="Rule Name"
        dataTest="ruleNameInput"
        :value="rule.name"
        :rows="1"
        placeholder="Enter name/label..."
        @input="updateRuleField('name', $event)"
      />

      <FormWysiwyg
        v-if="isRulesFieldVisible('ruleDescription')"
        :value="rule.description"
        :disabled="!isRulesFieldEditable('ruleDescription')"
        :config="{
          disableExtensions: ['embed', 'file']
        }"
        label="Rule Description"
        placeholder="This is rule description"
        class="ruleDescriptionEditor"
        @input="updateRuleField('description', $event)"
      />

      <!-- The WYSIWYG editor has the iframe and file nodes disabled, because this content can causes unintended issues when displayed
      in the requisite card in the public catalog-->
      <FormWysiwyg
        v-if="isRulesFieldVisible('ruleNotes')"
        :value="rule.notes"
        :disabled="!isRulesFieldEditable('ruleNotes')"
        :config="{
          disableExtensions: ['embed', 'file']
        }"
        label="Rule Notes"
        placeholder="Add additional notes to this rule"
        class="ruleNoteEditor"
        @input="updateRuleField('notes', $event)"
      />
      <div class="row">
        <div
          v-if="isEditing"
          class="flex-grow"
        >
          <div class="d-flex">
            <AppButton
              v-if="!readOnly && isRulesFieldVisible('deleteRule')"
              variant="danger"
              @click="$emit('delete')"
            >
              <AppIcon
                class="mr-1"
                name="remove_circle"
              />
              Remove
            </AppButton>
            <AppButton
              class="ml-auto"
              @click="isEditing = false"
            >
              <AppIcon
                name="close"
                class="mr-1"
              />
              Stop Editing
            </AppButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Read Only Rule Display -->
    <SimpleRequisiteDisplay
      v-else
      :rule="rule"
      :depth="depth + 1"
      :disabled="disabled"
      :isRulesFieldVisible="isRulesFieldVisible"
      :titleInterpolation="titleInterpolation"
      @edit="isEditing=true"
    />

    <!-- Sub Rules -->
    <div v-if="shouldShowSubRules">
      <div
        v-if="isEditing && rule.condition === 'numberOf'"
        class="d-flex align-items-center"
      >
        <span>At least </span>
        <div v-if="readOnly">
          {{ rule.number }}
        </div>
        <FormNumber
          v-else
          type="number"
          class="px-2"
          min="0"
          :value="rule.number"
          @input="updateRuleField('number', $event)"
        />
        <span> of the following:</span>
      </div>

      <div
        v-for="(subRule, ruleIndex) in rule.subRules"
        :key="subRule.id"
        class="sub-rule"
      >
        <SimpleRequisite
          class="d-flex card card-body"
          :rule-data="subRule"
          :depth="depth + 1"
          :kind="kind"
          :disabled="disabled"
          :read-only="readOnly"
          :editing="editingRuleId === subRule.id"
          :isRulesFieldVisible="isRulesFieldVisible"
          :isRulesFieldEditable="isRulesFieldEditable"
          :titleInterpolation="titleInterpolation"
          @updatedRule="updateSubRule($event, ruleIndex)"
          @delete="deleteSubRule(ruleIndex)"
        />
        <SimpleRequisiteConditionSeparator
          v-if="rule.subRules.length -1 !== ruleIndex"
          :condition="rule.condition"
        />
      </div>

      <AppButton
        v-if="isEditing"
        class="pl-2 mb-3 ml-auto"
        @click="addSubRule"
      >
        <AppIcon
          class="material-icons pr-2"
          name="add_circle"
        />
        Add Sub Rule
      </AppButton>
    </div>

  </div>
</template>

<script>
import FormNumber from '@/components/Forms/FormNumber'
import FormText from '@/components/Forms/FormText'
import FormTextarea from '@/components/Forms/FormTextarea'
import FormWysiwyg from '@/components/Forms/FormWysiwyg'
import FormSelect from '@/components/Forms/FormSelect'
import FormLabel from '@/components/Forms/FormLabel'
import SimpleRequisiteDataSelect from './SimpleRequisiteDataSelect'
import SimpleRequisiteDisplay from './SimpleRequisiteDisplay'
import SimpleRequisiteConditionSeparator from './SimpleRequisiteConditionSeparator'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { CM_VARIABLE_COURSES_AND_CREDITS_RULE } from '@shared/constants/featureFlags'

const CONDITION_OPTIONS = [
  'completedAllOf',
  'completedAtLeastXOf',
  'completedAnyOf',
  'enrolledIn',
  'minimumCredits',
  'minimumResidencyCredits',
  'minimumGrade',
  'averageGrade',
  'freeformText',
]

const DATA_SELECT_CONDITIONS = [
  'completed',
  'completedAllOf',
  'completedAnyOf',
  'completedAtLeastXOf',
  'enrolledIn',
  'minimumCredits',
  'minimumResidencyCredits',
  'minimumGrade',
  'averageGrade',
]

const GRADE_CONDITIONS = [
  'minimumGrade',
  'averageGrade',
]

const CONDITION_LABEL = {
  anyOf: 'Fulfill ANY of the following requirements',
  allOf: 'Fulfill ALL of the following requirements',
  completedAllOf: 'Complete All Of',
  completedAtLeastXOf: 'Complete At Least X Of',
  completedAnyOf: 'Complete Any Of',
  enrolledIn: 'Enroll In',
  completeVariableCoursesAndVariableCredits: 'Complete Variable Courses and Variable Credits',
}

export default {
  name: 'SimpleRequisite',
  components: {
    SimpleRequisiteDataSelect,
    FormNumber,
    FormSelect,
    FormText,
    FormLabel,
    FormTextarea,
    SimpleRequisiteDisplay,
    SimpleRequisiteConditionSeparator,
    FormWysiwyg,
  },
  props: {
    kind: { kind: String },
    depth: { type: Number, default: 0 },
    ruleData: { type: Object },
    disabled: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
    isProgram: { type: Boolean, default: false },
    isRulesFieldVisible: {
      type: Function,
      default: () => false,
    },
    isRulesFieldEditable: {
      type: Function,
      default: () => false,
    },
    titleInterpolation: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      conditionOptions: this.showVariableCoursesCreditsRule ? [ ...CONDITION_OPTIONS, 'completeVariableCoursesAndVariableCredits' ] : [ ...CONDITION_OPTIONS ],
      rule: this.ruleData || this.defaultRule,
      isEditing: this.editing,
      editingRuleId: null,
    }
  },
  setup () {
    const { showVariableCoursesCreditsRule } = useFeatureFlags({
      showVariableCoursesCreditsRule: CM_VARIABLE_COURSES_AND_CREDITS_RULE,
    })
    return { showVariableCoursesCreditsRule }
  },
  computed: {
    shouldShowSubRules () {
      return [ 'anyOf', 'allOf', 'numberOf' ].indexOf(this.rule.condition) !== -1
    },
    shouldShowGradeInputs () {
      return GRADE_CONDITIONS.includes(this.rule.condition)
    },
    shouldShowDataSelect () {
      return DATA_SELECT_CONDITIONS.includes(this.rule.condition)
    },
    defaultRule () {
      return {
        condition: 'completedAllOf',
        value: undefined,
        subRules: undefined,
        number: undefined,
        gradeType: undefined,
        grade: undefined,
        credits: undefined,
      }
    },
  },
  created () {
    if (this.depth < 3) this.conditionOptions.push('anyOf', 'allOf', 'numberOf')
  },
  methods: {
    conditionLabel (val) {
      return CONDITION_LABEL[val] || this.decamelize(val)
    },
    deleteSubRule (ruleIndex) {
      this.$delete(this.rule.subRules, ruleIndex)
      this.$emit('updatedRule', this.rule)
    },
    addSubRule () {
      const id = this.generateToken(8)
      this.rule = { ...this.rule, subRules: [ ...(this.rule.subRules || []), { ...this.defaultRule, id } ] }
      this.editingRuleId = id
      this.$emit('updatedRule', this.rule)
    },
    updateSubRule (subRule, ruleIndex) {
      this.rule.subRules[ruleIndex] = subRule
      this.$emit('updatedRule', this.rule)
    },
    updateRuleField (key, value) {
      this.rule = { ...this.rule, [key]: value }
      if (key === 'condition') this.updateRuleCondition(value)
      this.$emit('updatedRule', this.rule)
    },
    updateRuleCondition (condition) {
      if (this.shouldShowSubRules) {
        if (!this.rule.subRules) {
          this.rule.subRules = []
        }
      } else {
        this.rule.subRules = undefined
      }

      if (condition === 'numberOf') {
        this.rule.number = 1
      } else {
        this.rule.number = undefined
      }

      if (this.shouldShowDataSelect) {
        if (typeof this.rule.value !== 'object') {
          this.rule.value = {
            condition: '',
            values: [],
            number: undefined,
            subSelections: undefined,
          }
        }
      } else {
        this.rule.value = ''
      }
    },
  },
}
</script>

<style scoped>
.multiselect {
  white-space: nowrap;
}
</style>
